import { useState } from 'react';
import { ArrowLeft, Plus, Megaphone, AlertTriangle, Bell, PartyPopper, MoreVertical, Pencil, Trash2, Power } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { ptBR } from 'date-fns/locale';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

import {
  useAllAnnouncements,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
  Announcement,
} from '@/hooks/useAnnouncements';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';

type Priority = 'normal' | 'urgent' | 'celebration';

interface AnnouncementFormData {
  title: string;
  content: string;
  priority: Priority;
  expires_at: string;
}

const priorityConfig = {
  urgent: {
    icon: AlertTriangle,
    label: 'Urgente',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    badge: 'bg-red-100 text-red-700',
  },
  normal: {
    icon: Bell,
    label: 'Normal',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
  },
  celebration: {
    icon: PartyPopper,
    label: 'Celebração',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
  },
};

/** Parse 'YYYY-MM-DD' or 'YYYY-MM-DDT...' como data local (evita shift UTC) */
function parseDateLocal(dateStr: string): Date {
  const d = dateStr.split('T')[0];
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day);
}

const defaultFormData: AnnouncementFormData = {
  title: '',
  content: '',
  priority: 'normal',
  expires_at: '',
};

export default function AnnouncementsSettings() {
  const navigate = useNavigate();
  const { data: announcements, isLoading } = useAllAnnouncements();
  const { data: appUser } = useCurrentAppUser();
  const createMutation = useCreateAnnouncement();
  const updateMutation = useUpdateAnnouncement();
  const deleteMutation = useDeleteAnnouncement();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState<AnnouncementFormData>(defaultFormData);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleOpenCreate = () => {
    setEditingAnnouncement(null);
    setFormData(defaultFormData);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content || '',
      priority: announcement.priority,
      expires_at: announcement.expires_at ? announcement.expires_at.split('T')[0] : '',
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    try {
      if (editingAnnouncement) {
        await updateMutation.mutateAsync({
          id: editingAnnouncement.id,
          title: formData.title,
          content: formData.content || null,
          priority: formData.priority,
          expires_at: formData.expires_at ? `${formData.expires_at}T23:59:59` : null,
        });
        toast.success('Aviso atualizado com sucesso');
      } else {
        await createMutation.mutateAsync({
          title: formData.title,
          content: formData.content || null,
          priority: formData.priority,
          expires_at: formData.expires_at ? `${formData.expires_at}T23:59:59` : null,
          created_by: appUser?.id,
        });
        toast.success('Aviso criado com sucesso');
      }
      setIsDialogOpen(false);
      setFormData(defaultFormData);
      setEditingAnnouncement(null);
    } catch (error) {
      toast.error('Erro ao salvar aviso');
    }
  };

  const handleToggleActive = async (announcement: Announcement) => {
    try {
      await updateMutation.mutateAsync({
        id: announcement.id,
        is_active: !announcement.is_active,
      });
      toast.success(announcement.is_active ? 'Aviso desativado' : 'Aviso ativado');
    } catch (error) {
      toast.error('Erro ao atualizar aviso');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteMutation.mutateAsync(deleteId);
      toast.success('Aviso excluído com sucesso');
      setDeleteId(null);
    } catch (error) {
      toast.error('Erro ao excluir aviso');
    }
  };

  const getCreatorName = (announcement: Announcement) => {
    if (announcement.created_by_user) {
      return announcement.created_by_user.display_name || announcement.created_by_user.first_name || 'Usuário';
    }
    return 'Sistema';
  };

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/Settings')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Megaphone className="h-6 w-6 text-amber-600" />
                Gerenciar Avisos
              </h1>
              <p className="text-slate-600 text-sm">
                Crie e gerencie comunicados internos para a equipe
              </p>
            </div>
          </div>
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Aviso
          </Button>
        </div>

        {/* Lista de Avisos */}
        <div className="space-y-4">
          {isLoading ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                Carregando avisos...
              </CardContent>
            </Card>
          ) : !announcements?.length ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-muted-foreground">Nenhum aviso cadastrado</p>
                <Button variant="outline" className="mt-4" onClick={handleOpenCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar primeiro aviso
                </Button>
              </CardContent>
            </Card>
          ) : (
            announcements.map((announcement) => {
              const config = priorityConfig[announcement.priority as Priority] || priorityConfig.normal;
              const Icon = config.icon;
              const isExpired = announcement.expires_at && announcement.expires_at.split('T')[0] < new Date().toISOString().split('T')[0];

              return (
                <Card
                  key={announcement.id}
                  className={`${config.border} ${!announcement.is_active || isExpired ? 'opacity-60' : ''}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 ${config.bg} rounded-lg`}>
                          <Icon className={`h-5 w-5 ${config.color}`} />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{announcement.title}</CardTitle>
                          {announcement.content && (
                            <CardDescription className="mt-1 text-sm whitespace-pre-wrap">
                              {announcement.content}
                            </CardDescription>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={config.badge}>
                          {config.label}
                        </Badge>
                        {!announcement.is_active && (
                          <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                            Inativo
                          </Badge>
                        )}
                        {isExpired && (
                          <Badge variant="secondary" className="bg-red-100 text-red-600">
                            Expirado
                          </Badge>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenEdit(announcement)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(announcement)}>
                              <Power className="h-4 w-4 mr-2" />
                              {announcement.is_active ? 'Desativar' : 'Ativar'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteId(announcement.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>
                        Criado por {getCreatorName(announcement)} em{' '}
                        {safeFormat(new Date(announcement.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </span>
                      {announcement.expires_at && (
                        <span>
                          • Expira em{' '}
                          {safeFormat(parseDateLocal(announcement.expires_at), 'dd/MM/yyyy', { locale: ptBR })}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Dialog de Criação/Edição */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? 'Editar Aviso' : 'Novo Aviso'}
            </DialogTitle>
            <DialogDescription>
              {editingAnnouncement
                ? 'Atualize as informações do aviso'
                : 'Crie um novo comunicado para a equipe'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                placeholder="Ex: Manutenção programada"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Conteúdo</Label>
              <Textarea
                id="content"
                placeholder="Detalhes do aviso..."
                rows={3}
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Prioridade</Label>
              <Select
                value={formData.priority}
                onValueChange={(value: Priority) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4 text-blue-600" />
                      Normal
                    </div>
                  </SelectItem>
                  <SelectItem value="urgent">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      Urgente
                    </div>
                  </SelectItem>
                  <SelectItem value="celebration">
                    <div className="flex items-center gap-2">
                      <PartyPopper className="h-4 w-4 text-amber-600" />
                      Celebração
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires_at">Data de Expiração (opcional)</Label>
              <Input
                id="expires_at"
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Deixe em branco para não expirar
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingAnnouncement ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir aviso?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O aviso será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
